import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const PENDING_REMOVAL_MS = 5000;

type _Options = {
    /** Called with every pending id when the timer runs out. The ids are `take()`n: the caller must `release()` or `requeue()` them. */
    onExpire: (ids: string[]) => void;
    /** Called with the pending ids when the component unmounts before the timer ran out. */
    onFlush: (ids: string[]) => void;
    timeoutMs?: number;
};

/**
 * Holds the ids of deleted-but-not-yet-saved review rows. Ids, timer and callbacks live in refs so that `take()` is synchronous and
 * the unmount flush never sees a stale closure; the ids are mirrored into state only so the UI can hide the rows.
 *
 * An id is in one of three places: pending (inside the undo window, Undo is offered), in flight (taken by a save that has not
 * answered yet: still hidden, no Undo) or gone. `hiddenIds` is pending + in flight, so a deleted row never flashes back while its
 * save is on the wire; `release()` ends the flight (success: the saved tree no longer has the row; failure: the row is back).
 */
export const usePendingRemoval = ({ onExpire, onFlush, timeoutMs = PENDING_REMOVAL_MS }: _Options) => {
    const idsRef = useRef<Set<string>>(new Set());
    const inFlightRef = useRef<Set<string>>(new Set());
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const pausedRef = useRef(false);
    const unmountedRef = useRef(false);
    const lastNameRef = useRef<string | undefined>(undefined);
    const onExpireRef = useRef(onExpire);
    const onFlushRef = useRef(onFlush);
    onExpireRef.current = onExpire;
    onFlushRef.current = onFlush;

    const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());
    const [inFlightIds, setInFlightIds] = useState<ReadonlySet<string>>(() => new Set());
    const [lastName, setLastName] = useState<string | undefined>(undefined);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== undefined) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    const take = useCallback((): string[] => {
        clearTimer();
        const ids = Array.from(idsRef.current);
        idsRef.current = new Set();
        ids.forEach((id) => inFlightRef.current.add(id));
        inFlightRef.current = new Set(inFlightRef.current);
        setPendingIds(idsRef.current);
        setInFlightIds(inFlightRef.current);
        setLastName(undefined);
        return ids;
    }, [clearTimer]);

    // (Re)starts the undo window. Nothing runs while paused; `resume()` starts a fresh window.
    const arm = useCallback(() => {
        clearTimer();
        if (pausedRef.current || unmountedRef.current || idsRef.current.size === 0) return;
        timerRef.current = setTimeout(() => {
            timerRef.current = undefined;
            const ids = take();
            if (ids.length > 0) onExpireRef.current(ids);
        }, timeoutMs);
    }, [clearTimer, take, timeoutMs]);

    const remove = useCallback(
        (id: string, name: string) => {
            idsRef.current = new Set(idsRef.current).add(id);
            lastNameRef.current = name;
            setPendingIds(idsRef.current);
            setLastName(name);
            arm();
        },
        [arm]
    );

    const undo = useCallback(() => {
        clearTimer();
        idsRef.current = new Set();
        setPendingIds(idsRef.current);
        setLastName(undefined);
    }, [clearTimer]);

    /** A save that took `ids` has answered: they stop being hidden (saved out of the tree, or restored after a failure). */
    const release = useCallback((ids: string[]) => {
        const next = new Set(inFlightRef.current);
        ids.forEach((id) => next.delete(id));
        inFlightRef.current = next;
        setInFlightIds(next);
    }, []);

    /** The ids could not be saved now (another save is in flight): back into the undo window, to be retried when it closes. */
    const requeue = useCallback(
        (ids: string[]) => {
            release(ids);
            const next = new Set(idsRef.current);
            ids.forEach((id) => next.add(id));
            idsRef.current = next;
            setPendingIds(next);
            setLastName(lastNameRef.current);
            arm();
        },
        [arm, release]
    );

    /** Holds the timer (a confirm dialog is open and would act on a stale draft version if a save landed under it). */
    const pause = useCallback(() => {
        pausedRef.current = true;
        clearTimer();
    }, [clearTimer]);

    const resume = useCallback(() => {
        pausedRef.current = false;
        arm();
    }, [arm]);

    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            clearTimer();
            const ids = Array.from(idsRef.current);
            idsRef.current = new Set();
            if (ids.length > 0) onFlushRef.current(ids);
        };
    }, [clearTimer]);

    const hiddenIds = useMemo<ReadonlySet<string>>(() => {
        if (inFlightIds.size === 0) return pendingIds;
        const all = new Set(pendingIds);
        inFlightIds.forEach((id) => all.add(id));
        return all;
    }, [pendingIds, inFlightIds]);

    return { pendingIds, hiddenIds, lastName, remove, undo, take, release, requeue, pause, resume };
};
